import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { setToken as setStoredToken, getToken as getStoredToken, clearToken } from '../services/tokenService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getStoredToken());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, portal = 'member') => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password, portal });
      const { access_token, user: userData } = response.data;
      setToken(access_token);
      setUser(userData);
      setStoredToken(access_token);
      return { success: true, user: userData };
    } catch (error) {
      // Messages d'erreur améliorés selon le code de statut
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      
      let errorMessage = 'Une erreur est survenue lors de la connexion';
      
      if (status === 401) {
        errorMessage = 'Email ou mot de passe incorrect. Veuillez vérifier vos identifiants.';
      } else if (status === 403) {
        if (detail?.includes('désactivé')) {
          errorMessage = 'Votre compte a été désactivé. Veuillez contacter l\'administrateur.';
        } else if (detail?.includes('attente')) {
          errorMessage = 'Votre compte est en attente de validation par l\'administrateur.';
        } else {
          errorMessage = detail || 'Accès refusé. Veuillez contacter l\'administrateur.';
        }
      } else if (status === 404) {
        errorMessage = 'Aucun compte trouvé avec cet email. Veuillez vous inscrire.';
      } else if (status === 429) {
        // Rate limiting - extraire les raisons si disponibles
        const reasons = error.response?.data?.reasons || [];
        if (reasons.includes('bot_detected')) {
          errorMessage = 'Activité suspecte détectée. Veuillez rafraîchir la page et réessayer.';
        } else {
          errorMessage = 'Trop de tentatives de connexion. Veuillez attendre quelques minutes avant de réessayer.';
        }
      } else if (status === 500) {
        errorMessage = 'Erreur serveur. Veuillez réessayer plus tard.';
      } else if (!error.response) {
        errorMessage = 'Impossible de se connecter au serveur. Vérifiez votre connexion internet.';
      } else if (detail) {
        errorMessage = detail;
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const register = async (userData) => {
    try {
      await axios.post(`${API}/auth/register`, userData);
      return { success: true };
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      
      let errorMessage = 'Une erreur est survenue lors de l\'inscription.';
      
      if (status === 400) {
        if (detail?.includes('Email')) {
          errorMessage = 'Cet email est déjà utilisé. Veuillez vous connecter ou utiliser un autre email.';
        } else {
          errorMessage = detail || 'Données d\'inscription invalides.';
        }
      } else if (status === 409) {
        errorMessage = 'Un compte existe déjà avec cet email.';
      } else if (status === 429) {
        errorMessage = 'Trop de tentatives. Veuillez réessayer dans quelques minutes.';
      } else if (!error.response) {
        errorMessage = 'Impossible de se connecter au serveur. Vérifiez votre connexion internet.';
      } else if (detail) {
        errorMessage = detail;
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    clearToken();
    localStorage.removeItem('user');
    localStorage.removeItem('lastError');
    sessionStorage.clear();
  };

  // Fonction pour réinitialiser complètement l'état d'authentification
  const resetAuthState = () => {
    logout();
    // Force le rechargement pour nettoyer tout état React résiduel
    window.location.reload();
  };

  // Fonction pour définir directement les données d'auth (utilisé après login admin)
  const setAuthData = (accessToken, userData) => {
    setToken(accessToken);
    setUser(userData);
    setStoredToken(accessToken);
  };

  const getAuthHeader = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading, getAuthHeader, setAuthData, resetAuthState }}>
      {children}
    </AuthContext.Provider>
  );
};